import React from 'react';
import {
    Plus, Trash2, Globe
} from 'lucide-react';
import {
    Input
} from 'shared/components/ui/input';
import {
    Button
} from 'shared/components/ui/button';
import {
    Label
} from 'shared/components/ui/label';
import ResourcePicker from '../../components/ResourcePicker';

/**
 * DataConnectionsSection — assistant.data_connections editor.
 *
 * `data_connections` is an **API permission whitelist** on the assistant:
 *   [{ connection_id: <api id>, description: <free text> }, …]
 *
 * Effect: the assistant's opcodes are only allowed to call APIs whose ids
 * appear in this list. Empty list = no API calls allowed.
 *
 * UI: one row per entry. The connection is picked via ResourcePicker(kind:
 * 'api'); the description is a plain text input. "+ Add" appends a blank
 * row; trash removes one.
 */
export default function DataConnectionsSection({
    a, onPatch
}) {
    const rows = Array.isArray(a?.data_connections) ? a.data_connections : [];

    const setRows = (next) => onPatch({
        data_connections: next
    });
    const updateRow = (idx, patch) => setRows(rows.map((r, i) => (i === idx ? {
        ...r,
        ...patch
    } : r)));
    const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));
    const addRow = () => setRows([...rows, {
        connection_id: '',
        description: ''
    }]);

    return (
        <div className="space-y-3">
            <div className="text-[12.5px] text-muted-foreground">
                API permission whitelist for this assistant. Only the APIs listed below can be invoked
                from this assistant&apos;s opcodes. Each entry is a connection id (picked from the API
                Manager) plus a short description of why it&apos;s allowed.
            </div>

            {rows.length === 0 && (
                <div className="rounded-md border border-dashed bg-muted/30 px-4 py-6 text-center text-[12.5px] text-muted-foreground">
                    No data connections yet. This assistant cannot call any APIs from its opcodes.
                </div>
            )}

            <div className="space-y-2">
                {rows.map((row, i) => (
                    // Key by index — connection_id is editable, so keying on
                    // it would remount the row and steal focus.
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={i} className="rounded-md border bg-card p-3">
                        <div className="mb-2 flex items-center gap-2">
                            <Globe size={12} className="text-emerald-600" />
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                Connection #{i + 1}
                            </span>
                            <div className="flex-1" />
                            <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                onClick={() => removeRow(i)}
                                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                title="Remove connection"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-[260px_1fr]">
                            <div className="space-y-1">
                                <Label className="text-[11px] text-muted-foreground">API</Label>
                                <ResourcePicker
                                    kind="api"
                                    value={row.connection_id || ''}
                                    onChange={(id) => updateRow(i, {
                                        connection_id: id
                                    })}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-[11px] text-muted-foreground">Description</Label>
                                <Input
                                    value={row.description || ''}
                                    onChange={(e) => updateRow(i, {
                                        description: e.target.value
                                    })}
                                    placeholder="e.g. Weather API used by the weather-checker opcode"
                                    className="h-8 text-[12.5px]"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addRow}
                className="w-full border-dashed"
            >
                <Plus className="mr-1 h-3 w-3" /> Add data connection
            </Button>
        </div>
    );
}
